import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useLongPress } from '@uidotdev/usehooks';
import { getTableDisplayName, isMedia360Video, sortMediaWith360First, TDR } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import Location from 'iconsax-react/dist/esm/Location';
import { getCdnImageUri, getCdnVideoUri } from '../../helpers/getCDNURI';
import {
	AnnouncementCategoryLucideIconMap,
	getAnnouncementCategoryTitle,
	AnnouncementResponse
} from '../announcements/common';
import {
	ImageCard,
	ImageCardAsset
} from '../image-card/ImageCard';
import { LocalAnnouncementTag } from '../announcements/LocalAnnouncementTag';
import { CarouselCardInfo } from './CarouselCardInfo';
import { useScreenSize } from '../../hooks/useScreenSize';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { analytics, MEDIA_360_VIEW_HOVER, MEDIA_360_VIEW_LONG_PRESS } from '../../helpers/analytics';
import { PriceDisplay, PriceDisplayVariant } from '../__design-system/price-display/PriceDisplay';
import { useEstimateInvoice } from '../../hooks/useEstimateInvoice';
import { useTimeslot } from '../../api/getTimeslot';
import { useOutletContext } from 'react-router-dom';
import { AvailabilityGuardContext, SearchFilters } from '../../routes/AvailabilityGuard';
import VideoPlayer from '../video-player/VideoPlayer';

type CardShellProps = {
	restaurant: TDR.Restaurant;
	table: TDR.Table;
	searchFilters: SearchFilters;
	announcement?: AnnouncementResponse;
	viewedTable?: boolean;
	soldOutOverlay?: boolean;
	onCardClick: () => void;
	footer: React.ReactNode;
	timeslotData?: TDR.TimeSlotMap;
	/**
	 * The start time this card represents, when it is not simply the time the guest searched.
	 * A service-scoped section sets it so one table can render as two different products —
	 * a 10-hour Full Day at 09:00 and a 4-hour Half Day at 09:15 — each with its own price.
	 */
	slotTime?: string;
};

const CardShellInner = ({
	restaurant,
	table,
	searchFilters,
	announcement,
	viewedTable,
	soldOutOverlay = false,
	onCardClick,
	footer,
	timeslotData,
	slotTime
}: CardShellProps) => {
	const { t } = useTranslation();
	const cardRef = useRef<HTMLDivElement>(null);
	const imageContainerRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [debouncedViewedTable] = useDebounce(viewedTable, 1500);
	const [isHovering360View, setIsHovering360View] = useState(false);
	const [isPressing360View, setIsPressing360View] = useState(false);
	const longPressOccurredRef = useRef(false);
	const touchStartPositionRef = useRef<{ x: number; y: number } | null>(null);
	const hasMovedRef = useRef(false);
	const { isSmallScreen } = useScreenSize();
	const availabilityGuardContext = useOutletContext<AvailabilityGuardContext>();
	const featureFlags = availabilityGuardContext?.featureFlags;

	const { elementRef: intersectionRef } = useIntersectionObserver({
		threshold: 0.3,
		rootMargin: '0px'
	});

	const effectiveTime = slotTime || searchFilters.time;
	// Pricing and the timeslot lookup must agree on WHICH slot this card is about, so both read
	// the scoped time. Identity is preserved when unscoped so memoised consumers don't churn.
	const effectiveFilters = useMemo(
		() => (effectiveTime === searchFilters.time ? searchFilters : { ...searchFilters, time: effectiveTime }),
		[searchFilters, effectiveTime]
	);

	const shouldFetchIndividual = !timeslotData;
	const { data } = useTimeslot(
		shouldFetchIndividual ? table?.id : undefined,
		shouldFetchIndividual ? searchFilters.date : undefined,
		shouldFetchIndividual ? restaurant.id : undefined,
		shouldFetchIndividual ? effectiveTime : undefined,
		searchFilters.groupSize
	);

	const imageSrc = getCdnImageUri(table.carouselImagePath, {
		width: 359,
		fit: 'crop',
		dpr: 2
	});
	const mediaSorted = useMemo(() => sortMediaWith360First(table.media || []), [table.media]);
	const has360ViewMedia = !soldOutOverlay && mediaSorted.some(isMedia360Video);

	const get360ViewVideoSrc = () => {
		const video360Item = mediaSorted.find(isMedia360Video);
		return video360Item ? getCdnVideoUri(video360Item.path, {
			width: 359,
			fit: 'cover',
			duration: '10s'
		}) : null;
	};

	const shouldShow360View = has360ViewMedia && (
		(!isSmallScreen && isHovering360View) ||
		(isSmallScreen && isPressing360View)
	);

	useEffect(() => {
		if (!has360ViewMedia || !videoRef.current) {
			return;
		}

		const el = videoRef.current;
		let canPlayHandler: (() => Promise<void>) | null = null;

		const playVideo = async () => {
			if (!el.isConnected) {
				return;
			}

			try {
				if (el.readyState >= 2) {
					el.currentTime = 0;
					await el.play();
				}
				else {
					canPlayHandler = async () => {
						if (el.isConnected) {
							try {
								el.currentTime = 0;
								await el.play();
							}
							catch {
							}
						}
					};
					el.addEventListener('canplay', canPlayHandler);
				}
			}
			catch {
			}
		};

		if (shouldShow360View) {
			playVideo();
		}
		else if (el.isConnected) {
			try {
				el.pause();
				el.currentTime = 0;
			}
			catch {
			}
		}

		return () => {
			if (canPlayHandler) {
				el.removeEventListener('canplay', canPlayHandler);
			}
		};
	}, [shouldShow360View, has360ViewMedia]);

	useEffect(() => {
		if (debouncedViewedTable && cardRef.current && !isSmallScreen) {
			setTimeout(() => {
				if (!cardRef.current) {
					return;
				}

				let scrollContainer = cardRef.current.parentElement;
				while (scrollContainer && !scrollContainer.classList.contains('overflow-y-auto')) {
					scrollContainer = scrollContainer.parentElement;
				}

				if (scrollContainer) {
					const rect = cardRef.current.getBoundingClientRect();
					const containerRect = scrollContainer.getBoundingClientRect();
					const isInView = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

					if (!isInView) {
						const cardOffsetTop = cardRef.current.offsetTop;
						const containerHeight = scrollContainer.clientHeight;
						const cardHeight = cardRef.current.clientHeight;
						const scrollTop = cardOffsetTop - (containerHeight / 2) + (cardHeight / 2);

						scrollContainer.scrollTo({
							top: Math.max(0, scrollTop),
							behavior: 'smooth'
						});
					}
				}
				else {
					cardRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					});
				}
			}, 200);
		}
	}, [debouncedViewedTable, isSmallScreen, table.name]);

	useEffect(() => {
		if (!isPressing360View) {
			const timeoutId = setTimeout(() => {
				longPressOccurredRef.current = false;
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [isPressing360View]);

	const longPressHandlers = useLongPress(
		() => {
			if (has360ViewMedia && isSmallScreen && !hasMovedRef.current) {
				longPressOccurredRef.current = true;
				setIsPressing360View(true);
				analytics.track(MEDIA_360_VIEW_LONG_PRESS, {
					tableId: table.id,
					tableName: table.name,
					restaurantId: restaurant.id,
					restaurantName: restaurant.name
				});
			}
		},
		{
			onStart: () => {
				longPressOccurredRef.current = false;
				hasMovedRef.current = false;
			},
			onFinish: () => {
				longPressOccurredRef.current = false;
				setIsPressing360View(false);
				touchStartPositionRef.current = null;
				hasMovedRef.current = false;
			},
			onCancel: () => {
				longPressOccurredRef.current = false;
				setIsPressing360View(false);
				touchStartPositionRef.current = null;
				hasMovedRef.current = false;
			},
			threshold: 300
		}
	);

	const handleCardClick = (e: React.MouseEvent) => {
		if (longPressOccurredRef.current) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		onCardClick();
	};

	const handleVideoRef = useCallback((instance: HTMLVideoElement | null) => {
		videoRef.current = instance;
	}, []);

	const { estimatedInvoice } = useEstimateInvoice(restaurant, table, effectiveFilters);
	const timeslot = timeslotData?.[effectiveTime] || data?.timeSlot;

	return (
		<div ref={cardRef} data-testid="card-shell" className="bg-tablz-med-gray-3 h-fit hover:bg-tablz-med-gray-6 p-1 rounded-2xl border border-tablz-med-gray-4 shadow-[0px_1px_2px_#0000000D] max-w-[400px] sm:max-w-[400px] sm:min-w-[min(370px,90vw)] w-full cursor-pointer" onClick={handleCardClick}>
			<button
				className="w-full cursor-pointer"
				role="link"
				tabIndex={0}
				onKeyDown={(e) => ['Enter', ' '].includes(e.key) && onCardClick()}
				data-slug={table.slug}
				data-cy="tableCarouselItem"
				onClick={(e) => {
					if (longPressOccurredRef.current) {
						e.preventDefault();
						e.stopPropagation();
					}
				}}
			>
				<div
					ref={(el) => {
						intersectionRef.current = el;
						imageContainerRef.current = el;
					}}
					className={`relative isolate ${has360ViewMedia && isSmallScreen ? 'noselect' : ''}`}
					style={has360ViewMedia && isSmallScreen ? { touchAction: 'pan-y', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' } : {}}
					onMouseEnter={() => {
						if (has360ViewMedia && !isSmallScreen) {
							setIsHovering360View(true);
							analytics.track(MEDIA_360_VIEW_HOVER, {
								tableId: table.id,
								tableName: table.name,
								restaurantId: restaurant.id,
								restaurantName: restaurant.name
							});
						}
					}}
					onMouseLeave={() => has360ViewMedia && !isSmallScreen && setIsHovering360View(false)}
					onTouchStart={(e) => {
						if (has360ViewMedia && isSmallScreen && e.touches[0]) {
							touchStartPositionRef.current = {
								x: e.touches[0].clientX,
								y: e.touches[0].clientY
							};
							hasMovedRef.current = false;
						}
					}}
					onTouchMove={(e) => {
						if (has360ViewMedia && isSmallScreen && touchStartPositionRef.current && e.touches[0]) {
							const touch = e.touches[0];
							const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y);
							const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x);

							if (deltaY > 10 || deltaX > 10) {
								hasMovedRef.current = true;
								if (isPressing360View) {
									setIsPressing360View(false);
									longPressOccurredRef.current = false;
								}
							}
						}
					}}
					onTouchEnd={() => {
						touchStartPositionRef.current = null;
					}}
					{...(has360ViewMedia && isSmallScreen ? longPressHandlers : {})}
				>
					<ImageCard height={200} brightness={restaurant.brightness ?? 1} contrast={restaurant.contrast ?? 1}>
						{announcement && !soldOutOverlay && (
							<div className="absolute top-0 right-0 z-10">
								<LocalAnnouncementTag
									title={getAnnouncementCategoryTitle(announcement.category, t)}
									icon={AnnouncementCategoryLucideIconMap[announcement.category]}
									category={announcement.category}
									tableId={table?.id}
								/>
							</div>
						)}
						{has360ViewMedia ? (
							<>
								<ImageCardAsset src={imageSrc} alt={table.name} />
								<div
									className="absolute w-full h-full z-10 transition-opacity duration-200"
									style={{
										opacity: shouldShow360View ? 1 : 0
									}}
								>
									<VideoPlayer
										ref={handleVideoRef}
										src={get360ViewVideoSrc() || ''}
										onPlaybackChange={() => {}}
										hideControls={true}
										preload="auto"
										autoPlay={false}
										muted={true}
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
											borderRadius: '12px'
										}}
									/>
								</div>
							</>
						) : (
							<ImageCardAsset src={imageSrc} alt={table.name} />
						)}
						{soldOutOverlay && (
							<div className="absolute inset-0 bg-black/50 rounded-[16px] flex items-center justify-center z-10">
								<span className="font-poppins font-medium text-white text-[20px] leading-none">
									{t('inventory.sold_out')}
								</span>
							</div>
						)}
					</ImageCard>
					{viewedTable && !isSmallScreen && (
						<div className="absolute inset-0 bg-black bg-opacity-50 rounded-[16px] flex items-center justify-center">
							<Location size={40} color="#ffffff" />
						</div>
					)}
				</div>
			</button>
			<CarouselCardInfo
				table={table}
				name={getTableDisplayName(table)}
				priceElement={
					<PriceDisplay
						restaurant={restaurant}
						table={table}
						searchFilters={searchFilters}
						estimatedInvoice={estimatedInvoice}
						timeslot={timeslot}
						featureFlags={featureFlags}
						variant={PriceDisplayVariant.CAROUSEL}
					/>
				}
			/>
			<div onClick={(e) => e.stopPropagation()}>
				{footer}
			</div>
		</div>
	);
};

export const CardShell = memo(CardShellInner, (prev, next) => (
	prev.table.id === next.table.id
	&& prev.viewedTable === next.viewedTable
	&& prev.soldOutOverlay === next.soldOutOverlay
	&& prev.searchFilters?.time === next.searchFilters?.time
	&& prev.searchFilters?.date === next.searchFilters?.date
	&& prev.searchFilters?.groupSize === next.searchFilters?.groupSize
	&& prev.footer === next.footer
));
